import { useState } from 'react';
import PhotoAlbum from 'react-photo-album';
import { useTranslations } from 'next-intl';
import { useMediaQuery } from 'react-responsive';
import { MyCarousel } from '@/component/MyCarousel';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import dynamic from 'next/dynamic';

import styles from './Styles.module.css';

import ScreenLoader from '../ScreenLoader';

const Lightbox = dynamic(() => import('./Lightbox'), {
  ssr: false,
  loading: () => null,
});

const Gallery = ({ images, singleImage }) => {
  const t = useTranslations();
  const isMobile = useMediaQuery({ query: '(max-width: 639px)' });
  const [toggler, setToggler] = useState(false);
  const [show, setShow] = useState(false);
  const handleShow = () => {
    setShow(true);
  };
  const handleBack = () => {
    setShow(false);
  };

  const photos = images?.map((image) => ({
    alt: image.altText || '',
    title: image.caption || t('apartment.gallery.photo'),
    src: image.url,
    width: 450,
    height: 380,
  }));

  const photosSingle = images?.map((image) => ({
    alt: image.altText || '',
    title: image.caption || t('apartment.gallery.photo'),
    src: image.url,
    width: 334,
    height: 240,
  }));

  if (!images) {
    return <ScreenLoader />;
  }

  return (
    <div className={styles.gallery_grid}>
      {photos.length > 0 && (
        <div className="gallery-page">
          {!singleImage ? (
            <>
              <div className={styles.gallery_mobile_carousel}>
                <MyCarousel
                  images={images}
                  controls={false}
                  pagination={{
                    type: 'fraction',
                  }}
                  showActive={true}
                  onClick={handleShow}
                />
              </div>
              <PhotoAlbum
                layout="columns"
                photos={photos?.slice(0, isMobile ? 1 : 5)}
                columns={3}
                lazy={false}
                spacing={0}
                targetRowHeight={150}
                onClick={handleShow}
                renderPhoto={({
                  imageProps: { src, alt, style, width, ...restImageProps },
                }) => (
                  <div className="wrap-img relative">
                    <div className="bg-slate-50 absolute inset-0"></div>
                    <img
                      src={src}
                      alt={alt}
                      width={450}
                      height={380}
                      style={style}
                      {...restImageProps}
                      data-testid="photo-album-image"
                    />
                  </div>
                )}
              />
            </>
          ) : (
            <PhotoAlbum
              layout="rows"
              photos={photosSingle?.slice(0, 1)}
              columns={1}
              lazy={false}
              spacing={0}
              targetRowHeight={240}
              onClick={handleShow}
              renderPhoto={({
                imageProps: { src, alt, style, width, ...restImageProps },
              }) => (
                <div className="wrap-img relative">
                  <img
                    src={src}
                    alt={alt}
                    width={450}
                    height={380}
                    style={style}
                    {...restImageProps}
                    data-testid="photo-album-image"
                  />
                </div>
              )}
            />
          )}
          {photos && photos.length >= 4 && (
            <button
              className="btn btn-sm btn-icon btn-second btn-second-white !px-2.5 absolute right-3 bottom-3 max-sm:hidden"
              onClick={handleShow}
            >
              <i className="icon-widget text-[24px] mr-1.5" />
              <span>{t('apartment.gallery.link')}</span>
            </button>
          )}
          <Lightbox images={images} toggler={toggler} setToggler={setToggler} />
        </div>
      )}

      <Dialog
        className="gallery-model relative z-50"
        open={!!show}
        onClose={() => setShow(false)}
      >
        <div
          className="fixed inset-0 md:bg-primary-900/80"
          aria-hidden="true"
        />
        <div className="fixed inset-0 w-screen overflow-hidden">
          <div className="flex min-h-full items-center justify-center">
            <DialogPanel className="flex flex-col mx-auto w-full overflow-hidden min-h-full bg-white h-[100dvh]">
              <DialogTitle className="w-full flex max-md:px-2 max-md:py-2 md:p-3 md:justify-between">
                <button
                  type="button"
                  className="btn btn-icon"
                  onClick={handleBack}
                  data-testid="photo-album-back-button"
                >
                  <i className="icon-expand_left text-primary-800 max-md:text-3xl md:text-5xl" />
                </button>
              </DialogTitle>
              <div
                className="max-md:px-4 max-md:pt-0 md:px-[46px] py-8 overflow-auto"
                data-testid="photo-album-container"
              >
                <PhotoAlbum
                  layout="columns"
                  photos={photos}
                  lazy={false}
                  columns={2}
                  spacing={10}
                  targetRowHeight={150}
                  onClick={({ index }) => {
                    setToggler(!toggler ? `${index}` : false);
                  }}
                />
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Gallery;
